import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { CloudOff, ShoppingCart } from "lucide-react";
import { toast } from "sonner";
import { MTopBar, ComandaBar } from "@/components/mobile/MTopBar";
import { MPage, Loading, EmptyArt, ReasonDialog, cardCls } from "@/components/mobile/parts";
import { OrderGroupCard } from "@/components/mobile/OrderGroupCard";
import { useComanda } from "@/hooks/useMobileData";
import { useMobile } from "@/context/MobileContext";
import { useAuth } from "@/context/AuthContext";
import { api, brl, canDo, errMsg } from "@/lib/api";
import { orderGroups, reprintPayload, sendPrint, comandaTitle } from "@/lib/mobile";
import { cn } from "@/lib/utils";

function Waiting({ entries }) {
  if (!entries.length) return null;
  return (
    <section className={cn(cardCls, "border-l-4 border-amber-400 p-3 text-sm")} data-testid="m-orders-offline">
      <p className="mb-1 flex items-center gap-1.5 font-semibold text-amber-800"><CloudOff className="h-4 w-4" />Aguardando internet</p>
      {entries.map((e) => <p key={e.id} className="text-slate-600">{(e.lines || []).join(", ")} · {brl(e.total)}</p>)}
    </section>
  );
}

export default function MComandaOrders() {
  const { id } = useParams();
  const { c, load, cid } = useComanda(id);
  const { pendingFor } = useMobile();
  const { session } = useAuth();
  const [pend, setPend] = useState([]);
  const [target, setTarget] = useState(null);
  const canCancel = canDo(session.user, "cancel_sale");
  const live = c && !c.offline;
  useEffect(() => { if (live) api.get(`/approvals?comanda_id=${c.id}`).then((r) => setPend(r.data)).catch(() => {}); }, [c, live]);
  if (!c) return <><MTopBar title="Pedidos" back={`/m/comanda/${id}`} /><Loading /></>;
  const waitIds = new Set(pend.flatMap((a) => [a.item_id, a.order_id]).filter(Boolean));
  const groups = orderGroups(c);
  const open = c.status === "open";

  const confirm = async (reason) => {
    const { item, order } = target;
    try {
      if (canCancel) {
        await api.post(item ? `/comandas/${c.id}/items/${item.id}/cancel` : `/comandas/${c.id}/orders/${order.id}/cancel`, { reason });
        toast.success(item ? "Item cancelado" : "Pedido cancelado");
      } else {
        await api.post("/approvals", { type: item ? "cancel_item" : "cancel_order", comanda_id: c.id, item_id: item?.id || "", order_id: order?.id || "", reason });
        toast.success("Pedido enviado ao gerente. Acompanhe em Liberação.");
      }
      setTarget(null);
      load();
    } catch (e) { toast.error(errMsg(e)); }
  };
  const reprint = (g) => sendPrint("order", `2ª via · Pedido ${g.order.number} · ${comandaTitle(c)}`, reprintPayload(c, g.order, g.items))
    .then(() => toast.success("Enviado para a impressora do caixa")).catch((e) => toast.error(errMsg(e)));

  return (
    <>
      <MTopBar title="Ver Pedidos" back={`/m/comanda/${id}`} />
      <ComandaBar c={c} />
      <MPage>
        <Waiting entries={pendingFor(cid)} />
        {!groups.length && !pendingFor(cid).length && <EmptyArt icon={ShoppingCart} title="Nenhum pedido ainda" text="Use 'Adicionar Itens' para lançar o primeiro pedido." testId="m-orders-empty" />}
        {groups.map((g) => (
          <OrderGroupCard key={g.order?.id || "loose"} g={g} open={open} waitIds={waitIds} onReprint={reprint}
            onCancelItem={(item) => setTarget({ item })} onCancelOrder={(gr) => setTarget({ order: gr.order })} />
        ))}
        {groups.length > 0 && <p className="text-right font-display text-lg font-bold" data-testid="m-orders-total">Total {brl(c.total)}</p>}
      </MPage>
      <ReasonDialog open={!!target} required={!canCancel} confirmText={canCancel ? "Cancelar agora" : "Pedir ao gerente"}
        title={target?.item ? `Cancelar ${target.item.name}?` : `Cancelar o pedido ${target?.order?.number}?`}
        description={canCancel ? "O cancelamento fica registrado no histórico da comanda." : "Você não pode cancelar sozinho: o pedido vai para o gerente liberar."}
        onClose={() => setTarget(null)} onConfirm={confirm} testId="m-cancel" />
    </>
  );
}
